import { FC, FocusEvent, ReactNode, useRef } from 'react';
import { Box } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, parse, isValid } from 'date-fns';

export interface FormDatePickerProps extends Omit<
  DatePickerProps<true>,
  'value' | 'onChange' | 'onBlur'
> {
  value?: string | null; // ISO date string (YYYY-MM-DD) or null
  onChange?: (value: string | null) => void; // Returns ISO date string or null
  onBlur?: () => void; // Callback for when field loses focus (for form validation)
  /** Passed through to the text field (no need for slotProps.textField) */
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  fullWidth?: boolean;
  warningText?: string;
  /** Shown below the field when there is no error (e.g. "MM/dd/yyyy"). When error, helperText is shown instead. */
  formatHint?: ReactNode;
}

/**
 * A reusable DatePicker component that properly handles blur events for form validation.
 * This component wraps the MUI DatePicker and uses requestAnimationFrame to detect
 * when focus has actually left the DatePicker component (including its calendar icon button).
 *
 * This is particularly useful with React Hook Form's `mode: 'onBlur'` validation,
 * as it ensures validation only triggers when focus truly leaves the field, not when
 * moving between the textField and calendar icon button.
 */
export const FormDatePicker: FC<FormDatePickerProps> = ({
  value,
  onChange,
  onBlur,
  error,
  helperText,
  required,
  fullWidth,
  formatHint,
  warningText,
  ...datePickerProps
}) => {
  // Track if the DatePicker popup is open to prevent validation when opening
  const isPickerOpenRef = useRef(false);

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!onBlur) return;

    const currentTarget = e.currentTarget;

    // If the picker is open, don't validate (user is interacting with calendar)
    if (isPickerOpenRef.current) {
      return;
    }

    // Give browser time to focus the next element
    // This handles both tabbing and clicking scenarios reliably
    requestAnimationFrame(() => {
      const activeElement = document.activeElement as HTMLElement | null;

      // Check if focus is still within the DatePicker container
      if (activeElement && currentTarget.contains(activeElement)) {
        // Focus is still within the DatePicker - don't validate
        return;
      }

      // Focus has left the DatePicker - trigger validation
      onBlur();
    });
  };

  const handleChange = (newValue: unknown) => {
    if (!onChange) return;

    const dateValue = newValue as Date | null;

    // Picker can pass an invalid Date (e.g. partial input); format() throws RangeError
    if (dateValue === null || !isValid(dateValue)) {
      onChange('');
      return;
    }

    // Always pass a string so Zod sees string type; invalid dates trigger refine message
    onChange(format(dateValue, 'yyyy-MM-dd'));
  };

  // Convert ISO string to Date
  // Parse the ISO date string (YYYY-MM-DD) to a Date object
  const dateValue = value
    ? (() => {
        try {
          const parsed = parse(value, 'yyyy-MM-dd', new Date());
          return isValid(parsed) ? parsed : null;
        } catch {
          return null;
        }
      })()
    : null;

  const hasWarning = !error && !!warningText;

  const existingTextFieldProps = datePickerProps.slotProps?.textField || {};
  const existingSx =
    typeof existingTextFieldProps === 'object' && 'sx' in existingTextFieldProps
      ? existingTextFieldProps.sx
      : {};

  const mergedSlotProps = {
    ...datePickerProps.slotProps,
    textField: {
      ...existingTextFieldProps,
      required,
      fullWidth,
      error,
      helperText: !error && formatHint ? formatHint : helperText,
      ...(hasWarning && {
        helperText: warningText,
        FormHelperTextProps: { sx: { color: 'warning.main' } },
      }),
      sx: {
        // Ensure the TextField inside DatePicker matches the height of other inputs (48px)
        '& .MuiPickersOutlinedInput-root': {
          height: '48px !important',
          minHeight: '48px !important',
        },
        ...existingSx,
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box onBlur={handleBlur}>
        <DatePicker
          {...datePickerProps}
          slotProps={mergedSlotProps}
          value={dateValue}
          onChange={handleChange}
          onOpen={() => {
            // Mark picker as open to prevent validation
            isPickerOpenRef.current = true;
          }}
          onClose={() => {
            // Mark picker as closed
            isPickerOpenRef.current = false;
            // Trigger validation when picker closes
            onBlur?.();
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};
